import { useState, useEffect, useCallback } from "react";
import {
  Building2, Plus, Users, Key, Shield, Loader2, Search,
  ChevronDown, Save, X, Globe, Zap, CreditCard,
  Ban, Play,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { useToast } from "../components/ui/ToastModal";
import { useAuditLog } from "../hooks/useAuditLog";
import { logError } from "../lib/logError";

// ─── Types ───────────────────────────────────────────────
interface OrgRow {
  id: string;
  name: string;
  slug: string;
  is_internal: boolean;
  created_at: string;
  member_count: number;
  integration_count: number;
}

interface OrgIntegration {
  id: string;
  integration_type: string;
  is_active: boolean;
  created_at: string;
  config: Record<string, unknown>;
}

interface SubTier {
  id: string;
  name: string;
  display_name: string;
}

interface OrgSub {
  id: string;
  status: string;
  tier_id: string;
  tier: SubTier | null;
}

// ─── Component ───────────────────────────────────────────
export function SuperAdminOrgsPage() {
  const { addToast } = useToast();
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);

  // Create form
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [creating, setCreating] = useState(false);

  // Integration form
  const [showIntForm, setShowIntForm] = useState<string | null>(null);
  const [intClientId, setIntClientId] = useState("");
  const [intClientSecret, setIntClientSecret] = useState("");
  const [intScope, setIntScope] = useState("");
  const [intApiBase, setIntApiBase] = useState("https://api.sitemate.com/openapi/dashpivot/v1");
  const [intAuthUrl, setIntAuthUrl] = useState("https://auth.sitemate.com/oauth2/token");
  const [savingInt, setSavingInt] = useState(false);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      // Use RPC or raw query via supabase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("get_all_orgs_admin");

      if (error) {
        // Fallback: direct query (platform_admin RLS allows this)
        const { data: orgData, error: orgErr } = await (supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .from("organizations") as any)
          .select("id, name, slug, is_internal, created_at")
          .order("created_at", { ascending: true });

        if (orgErr) throw orgErr;

        const enriched: OrgRow[] = await Promise.all(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (orgData || []).map(async (o: any) => {
            const { count: mc } = await supabase
              .from("organization_members")
              .select("id", { count: "exact", head: true })
              .eq("organization_id", o.id);
            const { count: ic } = await (supabase
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .from as any)("organization_integrations")
              .select("id", { count: "exact", head: true })
              .eq("organization_id", o.id);
            return { ...o, member_count: mc || 0, integration_count: ic || 0 } as OrgRow;
          })
        );
        setOrgs(enriched);
        return;
      }

      setOrgs((data || []) as unknown as OrgRow[]);
    } catch (err: unknown) {
      logError("[SuperAdmin] Error:", err);
      addToast({ type: "error", title: "Failed to load organizations" });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  // ── Create Org ─────────────────────────────────────────
  const handleCreate = async () => {
    if (!newName.trim() || !newSlug.trim()) {
      addToast({ type: "error", title: "Name and slug are required" });
      return;
    }
    setCreating(true);
    try {
      const { error } = await supabase.from("organizations").insert({
        name: newName.trim(),
        slug: newSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, ""),
        is_internal: false,
      });
      if (error) throw error;
      addToast({ type: "success", title: `Organization "${newName}" created` });
      setNewName(""); setNewSlug(""); setShowCreate(false);
      fetchOrgs();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      addToast({ type: "error", title: "Failed to create organization", message: msg });
    } finally {
      setCreating(false);
    }
  };

  // ── Save Integration ───────────────────────────────────
  const handleSaveIntegration = async (orgId: string) => {
    if (!intClientId.trim() || !intClientSecret.trim()) {
      addToast({ type: "error", title: "Client ID and Secret are required" });
      return;
    }
    setSavingInt(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from as any)("organization_integrations").upsert(
        {
          organization_id: orgId,
          integration_type: "dashpivot",
          credentials: {
            client_id: intClientId.trim(),
            client_secret: intClientSecret.trim(),
            scope: intScope.trim(),
          },
          config: {
            api_base_url: intApiBase.trim(),
            auth_url: intAuthUrl.trim(),
            scope: intScope.trim(),
          },
          is_active: true,
        },
        { onConflict: "organization_id,integration_type" }
      );
      if (error) throw error;
      addToast({ type: "success", title: "Integration saved" });
      setShowIntForm(null);
      setIntClientId(""); setIntClientSecret(""); setIntScope("");
      fetchOrgs();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      addToast({ type: "error", title: "Failed to save integration", message: msg });
    } finally {
      setSavingInt(false);
    }
  };

  const filtered = orgs.filter(
    (o) =>
      o.name.toLowerCase().includes(search.toLowerCase()) ||
      o.slug.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "var(--spacing-3xl)" }}>
        <Loader2 size={32} style={{ animation: "spin 1s linear infinite", color: "var(--color-primary)" }} />
      </div>
    );
  }

  return (
    <div style={{ padding: "var(--spacing-xl)", maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--spacing-xl)" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-sm)", marginBottom: 4 }}>
            <Shield size={24} style={{ color: "var(--color-primary)" }} />
            <h1 style={{ margin: 0, fontSize: "var(--font-size-2xl)", fontWeight: 700, color: "var(--text-primary)" }}>
              Platform Administration
            </h1>
          </div>
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: "var(--font-size-sm)" }}>
            Manage organizations, integrations, and tenant configurations.
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreate(!showCreate)}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={16} /> New Organization
        </button>
      </div>

      {/* Stats Bar */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "var(--spacing-md)", marginBottom: "var(--spacing-xl)" }}>
        <StatCard icon={Building2} label="Organizations" value={orgs.length} color="#3b82f6" />
        <StatCard icon={Users} label="Total Members" value={orgs.reduce((s, o) => s + o.member_count, 0)} color="#8b5cf6" />
        <StatCard icon={Zap} label="Active Integrations" value={orgs.reduce((s, o) => s + o.integration_count, 0)} color="#10b981" />
      </div>

      {/* Create Form */}
      {showCreate && (
        <div style={{
          background: "var(--bg-card)", border: "1px solid var(--color-primary)", borderRadius: "var(--border-radius-lg)",
          padding: "var(--spacing-lg)", marginBottom: "var(--spacing-xl)",
        }}>
          <h3 style={{ margin: "0 0 var(--spacing-md)", color: "var(--text-primary)", fontSize: "var(--font-size-md)" }}>
            Create New Organization
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--spacing-md)", maxWidth: 600 }}>
            <div>
              <label style={labelStyle}>Organization Name</label>
              <input type="text" value={newName} onChange={(e) => { setNewName(e.target.value); setNewSlug(e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")); }} placeholder="Acme Construction" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Slug</label>
              <input type="text" value={newSlug} onChange={(e) => setNewSlug(e.target.value.replace(/[^a-z0-9-]/g, ""))} placeholder="acme-construction" style={inputStyle} />
            </div>
          </div>
          <div style={{ display: "flex", gap: "var(--spacing-sm)", marginTop: "var(--spacing-md)" }}>
            <button className="btn btn-primary" onClick={handleCreate} disabled={creating} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {creating ? <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> : <Plus size={14} />}
              Create
            </button>
            <button className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{ position: "relative", marginBottom: "var(--spacing-lg)" }}>
        <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search organizations…"
          style={{ ...inputStyle, paddingLeft: 36 }}
        />
      </div>

      {/* Org List */}
      <div style={{ display: "grid", gap: "var(--spacing-md)" }}>
        {filtered.map((org) => (
          <OrgCard
            key={org.id}
            org={org}
            expanded={expandedOrg === org.id}
            onToggle={() => setExpandedOrg(expandedOrg === org.id ? null : org.id)}
            onSetupIntegration={() => { setShowIntForm(org.id); setIntClientId(""); setIntClientSecret(""); setIntScope(""); }}
            showIntForm={showIntForm === org.id}
            intClientId={intClientId} setIntClientId={setIntClientId}
            intClientSecret={intClientSecret} setIntClientSecret={setIntClientSecret}
            intScope={intScope} setIntScope={setIntScope}
            intApiBase={intApiBase} setIntApiBase={setIntApiBase}
            intAuthUrl={intAuthUrl} setIntAuthUrl={setIntAuthUrl}
            savingInt={savingInt}
            onSaveIntegration={() => handleSaveIntegration(org.id)}
            onCancelIntForm={() => setShowIntForm(null)}
            addToast={addToast}
          />
        ))}
        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "var(--spacing-3xl)", color: "var(--text-muted)" }}>
            No organizations found.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Org Card ──────────────────────────────────────────────
function OrgCard({
  org, expanded, onToggle, onSetupIntegration, showIntForm,
  intClientId, setIntClientId, intClientSecret, setIntClientSecret,
  intScope, setIntScope,
  intApiBase, setIntApiBase, intAuthUrl, setIntAuthUrl,
  savingInt, onSaveIntegration, onCancelIntForm, addToast,
}: {
  org: OrgRow; expanded: boolean; onToggle: () => void;
  onSetupIntegration: () => void; showIntForm: boolean;
  intClientId: string; setIntClientId: (v: string) => void;
  intClientSecret: string; setIntClientSecret: (v: string) => void;
  intScope: string; setIntScope: (v: string) => void;
  intApiBase: string; setIntApiBase: (v: string) => void;
  intAuthUrl: string; setIntAuthUrl: (v: string) => void;
  savingInt: boolean; onSaveIntegration: () => void; onCancelIntForm: () => void;
  addToast: (t: Omit<import("../components/ui/ToastModal").Toast, "id">) => void;
}) {
  const [integrations, setIntegrations] = useState<OrgIntegration[]>([]);
  const [loadingInt, setLoadingInt] = useState(false);
  const [sub, setSub] = useState<OrgSub | null>(null);
  const [allTiers, setAllTiers] = useState<SubTier[]>([]);
  const [savingSub, setSavingSub] = useState(false);
  const { log } = useAuditLog();

  useEffect(() => {
    if (!expanded) return;
    setLoadingInt(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (supabase.from as any)("organization_integrations")
      .select("id, integration_type, is_active, created_at, config")
      .eq("organization_id", org.id)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then(({ data }: any) => { setIntegrations(data || []); setLoadingInt(false); });
    // Fetch subscription + tiers
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (supabase.from as any)("subscriptions")
      .select("id, status, tier_id, tier:subscription_tiers(id, name, display_name)")
      .eq("organization_id", org.id)
      .single()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then(({ data }: any) => { if (data) setSub(data); });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (supabase.from as any)("subscription_tiers")
      .select("id, name, display_name")
      .order("sort_order")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then(({ data }: any) => { if (data) setAllTiers(data); });
  }, [expanded, org.id]);

  const handleChangeTier = async (newTierId: string) => {
    if (!sub) return;
    setSavingSub(true);
    try {
      const oldTier = sub.tier?.display_name || sub.tier_id;
      const newTier = allTiers.find(t => t.id === newTierId);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from as any)("subscriptions").update({ tier_id: newTierId }).eq("id", sub.id);
      if (error) throw error;
      setSub({ ...sub, tier_id: newTierId, tier: newTier || null });
      addToast({ type: "success", title: `Tier changed to ${newTier?.display_name || "unknown"}` });
      log({ action: "subscription.upgrade", metadata: { old_tier: oldTier, new_tier: newTier?.display_name, organization_id: org.id } });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      addToast({ type: "error", title: "Failed to change tier", message: err.message });
    } finally { setSavingSub(false); }
  };

  const handleToggleStatus = async () => {
    if (!sub) return;
    setSavingSub(true);
    const newStatus = sub.status === "active" ? "paused" : "active";
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from as any)("subscriptions").update({ status: newStatus }).eq("id", sub.id);
      if (error) throw error;
      setSub({ ...sub, status: newStatus });
      addToast({ type: "success", title: `Subscription ${newStatus === "active" ? "activated" : "suspended"}` });
      log({ action: newStatus === "active" ? "subscription.activate" : "subscription.suspend", metadata: { status: newStatus, organization_id: org.id } });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      addToast({ type: "error", title: "Failed to update status", message: err.message });
    } finally { setSavingSub(false); }
  };

  return (
    <div style={{
      background: "var(--bg-card)", border: `1px solid ${expanded ? "var(--color-primary)" : "var(--border-color)"}`,
      borderRadius: "var(--border-radius-lg)", overflow: "hidden", transition: "border-color 0.2s ease",
    }}>
      {/* Header Row */}
      <div
        onClick={onToggle}
        style={{
          display: "flex", alignItems: "center", padding: "var(--spacing-md) var(--spacing-lg)",
          cursor: "pointer", gap: "var(--spacing-md)",
        }}
      >
        <div style={{
          width: 40, height: 40, borderRadius: "var(--border-radius-md)",
          background: org.is_internal ? "linear-gradient(135deg, #3b82f6, #8b5cf6)" : "linear-gradient(135deg, #06b6d4, #10b981)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 700, fontSize: "var(--font-size-sm)", flexShrink: 0,
        }}>
          {org.name[0]}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: "var(--font-size-sm)" }}>{org.name}</span>
            {org.is_internal && (
              <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 99, background: "rgba(139,92,246,0.12)", color: "#8b5cf6" }}>INTERNAL</span>
            )}
          </div>
          <div style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)" }}>{org.slug}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-lg)" }}>
          <MiniStat icon={Users} value={org.member_count} label="members" />
          <MiniStat icon={Key} value={org.integration_count} label="integrations" />
          <ChevronDown size={16} style={{ color: "var(--text-muted)", transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }} />
        </div>
      </div>

      {/* Expanded Detail */}
      {expanded && (
        <div style={{ borderTop: "1px solid var(--border-color)", padding: "var(--spacing-lg)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "var(--spacing-md)", marginBottom: "var(--spacing-lg)" }}>
            <DetailItem label="Organization ID" value={org.id} mono />
            <DetailItem label="Created" value={new Date(org.created_at).toLocaleDateString()} />
            <DetailItem label="Type" value={org.is_internal ? "Internal (Platform)" : "Client Tenant"} />
          </div>

          {/* ── Subscription Management ── */}
          <div style={{ marginBottom: "var(--spacing-lg)", padding: "var(--spacing-md)", background: "var(--bg-secondary)", borderRadius: "var(--border-radius-md)", border: "1px solid var(--border-color)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--spacing-sm)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <CreditCard size={16} style={{ color: "var(--color-primary)" }} />
                <h4 style={{ margin: 0, fontSize: "var(--font-size-sm)", fontWeight: 600, color: "var(--text-primary)" }}>Subscription</h4>
              </div>
              {org.is_internal && (
                <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: "rgba(139,92,246,0.12)", color: "#8b5cf6" }}>Internal · Unlimited</span>
              )}
            </div>
            {sub ? (
              <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-md)", flexWrap: "wrap" }}>
                {/* Status badge */}
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 99,
                  background: sub.status === "active" ? "rgba(16,185,129,0.1)" : sub.status === "paused" ? "rgba(245,158,11,0.1)" : "rgba(239,68,68,0.1)",
                  color: sub.status === "active" ? "#10b981" : sub.status === "paused" ? "#f59e0b" : "#ef4444",
                }}>
                  {sub.status.toUpperCase()}
                </span>
                {/* Tier selector */}
                <select
                  value={sub.tier_id}
                  onChange={(e) => handleChangeTier(e.target.value)}
                  disabled={savingSub}
                  style={{ height: 32, padding: "0 8px", border: "1px solid var(--border-color)", borderRadius: "var(--border-radius-md)", background: "var(--bg-card)", color: "var(--text-primary)", fontSize: "var(--font-size-xs)", cursor: "pointer" }}
                >
                  {allTiers.map(t => <option key={t.id} value={t.id}>{t.display_name}</option>)}
                </select>
                {/* Suspend / Activate button */}
                {!org.is_internal && (
                  <button
                    className={`btn ${sub.status === "active" ? "btn-ghost" : "btn-primary"}`}
                    onClick={handleToggleStatus}
                    disabled={savingSub}
                    style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "var(--font-size-xs)", padding: "4px 12px" }}
                  >
                    {savingSub ? <Loader2 size={12} style={{ animation: "spin 1s linear infinite" }} /> : sub.status === "active" ? <Ban size={12} /> : <Play size={12} />}
                    {sub.status === "active" ? "Suspend" : "Activate"}
                  </button>
                )}
              </div>
            ) : (
              <p style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)", margin: 0 }}>No subscription found.</p>
            )}
          </div>

          {/* Integrations Section */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--spacing-md)" }}>
            <h4 style={{ margin: 0, fontSize: "var(--font-size-sm)", fontWeight: 600, color: "var(--text-primary)" }}>
              Integrations
            </h4>
            <button className="btn btn-ghost" onClick={onSetupIntegration} style={{ fontSize: "var(--font-size-xs)", display: "flex", alignItems: "center", gap: 4 }}>
              <Plus size={14} /> Add Integration
            </button>
          </div>

          {loadingInt ? (
            <div style={{ padding: "var(--spacing-md)", textAlign: "center" }}>
              <Loader2 size={16} style={{ animation: "spin 1s linear infinite", color: "var(--text-muted)" }} />
            </div>
          ) : integrations.length === 0 ? (
            <div style={{
              padding: "var(--spacing-lg)", textAlign: "center", color: "var(--text-muted)",
              fontSize: "var(--font-size-xs)", background: "var(--bg-secondary)", borderRadius: "var(--border-radius-md)",
            }}>
              No integrations configured. Click "Add Integration" to connect Dashpivot.
            </div>
          ) : (
            <div style={{ display: "grid", gap: "var(--spacing-sm)" }}>
              {integrations.map((int) => (
                <div key={int.id} style={{
                  display: "flex", alignItems: "center", gap: "var(--spacing-md)",
                  padding: "var(--spacing-sm) var(--spacing-md)",
                  background: "var(--bg-secondary)", borderRadius: "var(--border-radius-md)",
                }}>
                  <Globe size={16} style={{ color: int.is_active ? "#10b981" : "var(--text-muted)" }} />
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 500, fontSize: "var(--font-size-sm)", color: "var(--text-primary)", textTransform: "capitalize" }}>
                      {int.integration_type}
                    </span>
                  </div>
                  {int.is_active ? (
                    <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: "rgba(16,185,129,0.1)", color: "#10b981" }}>Active</span>
                  ) : (
                    <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: "rgba(239,68,68,0.1)", color: "#ef4444" }}>Inactive</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Integration Form */}
          {showIntForm && (
            <div style={{ marginTop: "var(--spacing-md)", padding: "var(--spacing-md)", background: "var(--bg-secondary)", borderRadius: "var(--border-radius-md)", border: "1px solid var(--border-color)" }}>
              <h5 style={{ margin: "0 0 var(--spacing-sm)", fontSize: "var(--font-size-sm)", fontWeight: 600, color: "var(--text-primary)" }}>
                Dashpivot Integration
              </h5>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--spacing-sm)" }}>
                <div>
                  <label style={labelStyle}>Client ID</label>
                  <input type="text" value={intClientId} onChange={(e) => setIntClientId(e.target.value)} style={inputStyle} placeholder="OAuth Client ID" />
                </div>
                <div>
                  <label style={labelStyle}>Client Secret</label>
                  <input type="password" value={intClientSecret} onChange={(e) => setIntClientSecret(e.target.value)} style={inputStyle} placeholder="OAuth Client Secret" />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>Scope (Target Entity UUID)</label>
                  <input type="text" value={intScope} onChange={(e) => setIntScope(e.target.value)} style={inputStyle} placeholder="63067314-10ed-4e4f-b122-5dfbf75a33d2" />
                </div>
                <div>
                  <label style={labelStyle}>API Base URL</label>
                  <input type="text" value={intApiBase} onChange={(e) => setIntApiBase(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Auth URL</label>
                  <input type="text" value={intAuthUrl} onChange={(e) => setIntAuthUrl(e.target.value)} style={inputStyle} />
                </div>
              </div>
              <div style={{ display: "flex", gap: "var(--spacing-sm)", marginTop: "var(--spacing-sm)" }}>
                <button className="btn btn-primary" onClick={onSaveIntegration} disabled={savingInt} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "var(--font-size-xs)" }}>
                  {savingInt ? <Loader2 size={12} style={{ animation: "spin 1s linear infinite" }} /> : <Save size={12} />} Save
                </button>
                <button className="btn btn-ghost" onClick={onCancelIntForm} style={{ fontSize: "var(--font-size-xs)" }}>
                  <X size={12} /> Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color }: { icon: typeof Building2; label: string; value: number; color: string }) {
  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-color)", borderRadius: "var(--border-radius-lg)",
      padding: "var(--spacing-md)", display: "flex", alignItems: "center", gap: "var(--spacing-md)",
    }}>
      <div style={{ width: 40, height: 40, borderRadius: "var(--border-radius-md)", background: `${color}14`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div>
        <div style={{ fontSize: "var(--font-size-xl)", fontWeight: 700, color: "var(--text-primary)" }}>{value}</div>
        <div style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)" }}>{label}</div>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, value, label }: { icon: typeof Users; value: number; label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "var(--font-size-xs)", color: "var(--text-muted)" }}>
      <Icon size={14} /> {value} {label}
    </div>
  );
}

function DetailItem({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: "var(--font-size-sm)", fontWeight: 500, color: "var(--text-primary)", fontFamily: mono ? "monospace" : "inherit", wordBreak: "break-all" }}>
        {value}
      </div>
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  width: "100%", height: 36, padding: "0 var(--spacing-sm)",
  border: "1px solid var(--border-color)", borderRadius: "var(--border-radius-md)",
  background: "var(--bg-secondary)", color: "var(--text-primary)", fontSize: "var(--font-size-sm)",
};

const labelStyle: React.CSSProperties = {
  display: "block", fontSize: "var(--font-size-xs)", fontWeight: 500,
  color: "var(--text-muted)", marginBottom: 4,
};
